/* Slice 2I · Levi Unified Estimate Experience
 * ============================================
 * One primary contractor-facing AI surface on the estimate page. Presents
 * Levi as the single AI coworker helping with the job — coordinates the
 * pre-existing organizer, estimate-check, and Double Check ENGINES which
 * remain untouched behind their existing handlers.
 *
 * Design tenets (from the authorized product-owner spec):
 *   - "Levi recommends. The contractor decides."
 *   - "Unify the surface. Reuse the engines."
 *   - Present exactly ONE recommended next action at a time.
 *   - Secondary AI actions are hidden behind a compact disclosure so the
 *     estimate stays the visual centre of the page.
 *   - No new AI calls on render / field edit; every action is contractor-
 *     triggered via the existing handlers passed in as props.
 *   - Preserve everything Slice 2H shipped: approved-pricing precedence,
 *     duplicate suppression, quantity provenance, "nothing new to add"
 *     path — all of which live in the parent handlers already.
 */
import { useMemo, useState } from "react";
import { Sparkles, ShieldCheck, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const cls = (...xs) => xs.filter(Boolean).join(" ");

// Pure state resolver — reads existing estimate/project fields already
// populated by Slice 2H. Never issues network calls.
const deriveLeviState = ({ estimate, project, running, scopeBanner, doubleCheckStale }) => {
  const lines = (estimate && estimate.line_items) || [];
  const approved = lines.filter((li) => li && li.pricing_rule_id);
  const needsPricing = lines.filter((li) => li && (li.needs_pricing === true || li.needs_material_pricing === true));
  const notesPresent = Boolean(
    (project?.voice_transcript || "").trim()
    || (project?.typed_notes || "").trim()
    || (project?.job_description || "").trim()
    || (project?.measurements || "").trim()
    || (project?.material_notes || "").trim()
    || (project?.special_conditions || "").trim()
  );
  const dc = estimate && estimate.double_check;
  const dcRed = Number(dc && dc.red_count) || 0;
  const dcYellow = Number(dc && dc.yellow_count) || 0;

  // 1. Any in-flight action → surface that first.
  if (running?.scope) return { key: "organizing", primary: null };
  if (running?.check) return { key: "checking", primary: null };
  // 2. Contractor-visible failure from the most recent scope run (banner is opt-in).
  if (scopeBanner?.error) return { key: "organizer_failed", primary: "retry" };
  // 3. Fresh estimate, no lines yet.
  if (lines.length === 0) {
    return { key: "ready_to_organize", primary: notesPresent ? "organize" : null };
  }
  // 4. Estimate has lines but some still need pricing.
  if (needsPricing.length > 0) {
    return { key: "needs_pricing", primary: "review_missing", count: needsPricing.length };
  }
  // 5. Double Check has already run — surface its findings state.
  if (dc) {
    return { key: "double_check_findings", primary: null, red: dcRed, yellow: dcYellow, stale: doubleCheckStale };
  }
  // 6. Approved pricing present, no unpriced work, Double Check not yet run.
  if (approved.length > 0) {
    return { key: "approved_matched", primary: "run_double_check", name: approved[0].name };
  }
  // 7. Fallback — estimate has content, no strong signal.
  return { key: "ready_for_double_check", primary: "run_double_check" };
};

export const LeviCoworkerCard = ({
  estimate,
  project,
  running,
  scopeBanner,
  doubleCheckStale,
  onOrganize,
  onRunCheck,
  onRunDoubleCheck,
  onJumpToMissingMaterial,
  onJumpToWarnings,
  // Slice 2J · One Double Check Action.
  // When true, Levi does NOT present a competing "Run Double Check"
  // primary CTA — the deterministic Double Check card mounted below
  // Levi on the estimate page owns that action. Levi remains
  // explanatory (state message + secondary actions) so the contractor
  // still gets the recommendation copy, just not a duplicate button.
  // The handler, endpoint, engine, advisory toggle, and every other
  // Levi state are unchanged.
  suppressDoubleCheckAction = false,
  // Slice 2J correction · One Levi estimate-check.
  // The estimate page passes its unified estimate-check UI (the single
  // "Have Levi Check This Estimate" button, findings summary, and the
  // AI advisory toggle) as `estimateCheckSlot`. Levi renders it INSIDE
  // its own card so the contractor sees ONE Levi-owned surface, not
  // two adjacent cards. The slot is not a new engine — Estimates.jsx
  // still owns runDoubleCheck and the existing `run-double-check-btn`
  // testid so the Slice 2I "Double Check trigger remains contractor-
  // controlled and unchanged" invariant is preserved. Default `null`
  // preserves Levi's behavior everywhere else Levi is used.
  estimateCheckSlot = null,
}) => {
  const { t } = useLang();
  const [expanded, setExpanded] = useState(false);
  const state = useMemo(
    () => deriveLeviState({ estimate, project, running, scopeBanner, doubleCheckStale }),
    [estimate, project, running, scopeBanner, doubleCheckStale],
  );

  const stateMessage = (() => {
    switch (state.key) {
      case "organizing": return t("estimates_form.ai_organize_body_running");
      case "checking":   return t("estimates_form.ai_check_body_running");
      case "organizer_failed":       return t("estimates_form.levi_state_organizer_failed");
      case "ready_to_organize":      return t("estimates_form.levi_state_ready_to_organize");
      case "needs_pricing":          return t("estimates_form.levi_state_needs_pricing", state.count);
      case "approved_matched":       return t("estimates_form.levi_state_approved_pricing_matched", state.name || "");
      case "double_check_findings":  return t("estimates_form.levi_state_double_check_findings", state.red, state.yellow);
      case "ready_for_double_check": return t("estimates_form.levi_state_ready_for_double_check");
      default: return "";
    }
  })();

  // Primary CTA — exactly one at any time.
  // Slice 2J · When the deterministic Double Check card owns the
  // action below Levi on the estimate page, suppress Levi's
  // competing "Run Double Check" primary button. The state message
  // continues to render so Levi still explains what to do next.
  // Slice 2J amendment · When the estimate page passes an
  // estimateCheckSlot, Levi's SINGLE contractor-facing action is the
  // slot's "Have Levi Check This Estimate" button. Suppress every
  // derived-state primary CTA (organize / review_missing / retry /
  // run_double_check) so no competing contractor-facing Levi action
  // is presented. Any required internal processing (scope organizing,
  // review preparation, etc.) still runs on the backend when the
  // check is triggered.
  const suppressedPrimary = Boolean(estimateCheckSlot) || (suppressDoubleCheckAction && state.primary === "run_double_check");
  const primary = (() => {
    if (suppressedPrimary) return null;
    switch (state.primary) {
      case "organize":
        return {
          testid: "levi-primary-organize",
          label: t("estimates_form.levi_action_organize"),
          onClick: onOrganize,
          disabled: Boolean(running?.scope),
          icon: Sparkles,
        };
      case "review_missing":
        return {
          testid: "levi-primary-review-missing",
          label: t("estimates_form.levi_action_review_missing"),
          onClick: onJumpToMissingMaterial,
          disabled: false,
          icon: AlertTriangle,
        };
      case "run_double_check":
        return {
          testid: "levi-primary-double-check",
          label: t("estimates_form.levi_action_run_double_check"),
          onClick: onRunDoubleCheck,
          disabled: false,
          icon: ShieldCheck,
        };
      case "retry":
        return {
          testid: "levi-primary-retry",
          label: t("estimates_form.levi_action_retry"),
          onClick: onOrganize,
          disabled: Boolean(running?.scope),
          icon: Sparkles,
        };
      default:
        return null;
    }
  })();

  const PrimaryIcon = primary?.icon;

  return (
    <section
      data-testid="levi-coworker-card"
      className="jc-card p-5 space-y-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="overline flex items-center gap-1.5">
            <Sparkles size={12} className="text-[var(--jc-blue)]" />
            {t("estimates_form.levi_coworker_overline")}
          </div>
          <h2
            className="font-display text-lg mt-1"
            data-testid="levi-coworker-title"
          >
            {t("estimates_form.levi_coworker_title")}
          </h2>
        </div>
      </header>

      <div className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-surface)]/40 p-3 space-y-2">
        <div
          data-testid="levi-coworker-state"
          className="text-sm text-[var(--jc-text)]"
        >
          {stateMessage}
        </div>
        {primary && (
          <div className="pt-1 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
              {t("estimates_form.levi_recommends")}
            </span>
            <button
              type="button"
              onClick={primary.onClick}
              disabled={primary.disabled}
              data-testid={primary.testid}
              className={cls(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold",
                "jc-btn-primary disabled:opacity-60",
              )}
            >
              {PrimaryIcon && <PrimaryIcon size={14} />}
              {primary.label}
            </button>
            <span className="text-xs text-[var(--jc-text-soft)]">
              {t("estimates_form.levi_you_decide")}
            </span>
          </div>
        )}
      </div>

      {/* Slice 2J correction · Unified Levi estimate-check surface.
          Estimate page passes its "Have Levi Check This Estimate"
          button + findings summary here. Rendered as a natural
          extension of the Levi surface — one flat card, NOT nested
          inside a second bordered container. Any other caller (Levi
          elsewhere in the app) passes `null` and Levi keeps its
          unchanged Slice 2I presentation. */}
      {estimateCheckSlot && (
        <div
          data-testid="levi-estimate-check-slot"
          className="space-y-2 pt-1"
        >
          {estimateCheckSlot}
        </div>
      )}

      {/* Slice 2J correction §10 · Suppress the "More AI actions"
          secondary disclosure whenever the estimate-check slot is
          active. In that state the contractor already sees ONE
          recommended action (Have Levi Check This Estimate); a
          "More AI actions" chevron would reintroduce competing paths
          immediately after the review surface was simplified. Levi's
          unrelated states (organizer_failed, ready_to_organize, etc.)
          are unaffected — they never mount the estimate-check slot. */}
      {!estimateCheckSlot && (
      <div className="border-t border-[var(--jc-border)] pt-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="levi-secondary-toggle"
          className="inline-flex items-center gap-1 text-xs text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded
            ? t("estimates_form.levi_secondary_less")
            : t("estimates_form.levi_secondary_more")}
        </button>
        {expanded && (
          <div
            data-testid="levi-secondary-actions"
            className="mt-3 grid sm:grid-cols-2 gap-2"
          >
            <button
              type="button"
              onClick={onOrganize}
              disabled={Boolean(running?.scope)}
              data-testid="levi-secondary-organize"
              className="text-left rounded-md border border-[var(--jc-border)] px-3 py-2 hover:bg-[var(--jc-bg)] disabled:opacity-60"
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <Sparkles size={12} className="text-[var(--jc-blue)]" />
                {t("estimates_form.ai_organize_title")}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
                {running?.scope
                  ? t("estimates_form.ai_organize_body_running")
                  : t("estimates_form.ai_organize_body_idle")}
              </div>
            </button>
            <button
              type="button"
              onClick={onRunCheck}
              disabled={Boolean(running?.check)}
              data-testid="levi-secondary-check"
              className="text-left rounded-md border border-[var(--jc-border)] px-3 py-2 hover:bg-[var(--jc-bg)] disabled:opacity-60"
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <ShieldCheck size={12} className="text-[var(--jc-teal)]" />
                {t("estimates_form.ai_check_title")}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
                {running?.check
                  ? t("estimates_form.ai_check_body_running")
                  : t("estimates_form.ai_check_body_idle")}
              </div>
            </button>
          </div>
        )}
      </div>
      )}

      <p
        data-testid="levi-coworker-promise"
        className="text-xs text-[var(--jc-text-soft)] italic"
      >
        {t("estimates_form.levi_promise")}
      </p>
    </section>
  );
};

export default LeviCoworkerCard;
